import { Tooltip } from '@blueprintjs/core';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

const joi = require('joi');

const getRequiredStringSchema = (label) =>
  joi
    .string()
    .label(label)
    .required()
    .messages({ 'string.empty': '{{#label}} is required' });

const getRequiredNumberSchema = (label) => joi.number().label(label).required();

const getEmailSchema = (label, isRequired) => {
  if (isRequired) {
    return joi
      .string()
      .label(label || 'Email')
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.email': 'Enter a valid email address',
        'string.empty': '{{#label}} is required',
      });
  } else {
    return joi
      .string()
      .label(label || 'Email')
      .email({ tlds: { allow: false } })
      .required()
      .messages({ 'string.email': 'Enter a valid email address' });
  }
};

export const useValidation = (fields, object) => {
  const [validatedObject, setValidatedObject] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [blurredFields, setBlurredFields] = useState();

  let objectSchema = joi.object(
    Object.entries(fields).reduce((schemaObject, [key, row]) => {
      let schema = row.fieldSchema;

      if (!schema) {
        if (row.isEmail) {
          schema = getEmailSchema(row.label, row.isRequired);
        } else if (row.isRequired) {
          schema = row.isNumber
            ? getRequiredNumberSchema(row.label || key)
            : getRequiredStringSchema(row.label || key);
        }
      }

      if (schema) {
        schemaObject[key] = schema;
      }

      return schemaObject;
    }, {}),
  );

  useEffect(() => {
    blurredFields && validateObject();
  }, [blurredFields]);

  const validateObject = () => {
    if (objectSchema && object) {
      const { error, value } = objectSchema.validate(object, {
        abortEarly: false,
        allowUnknown: true,
        errors: { wrap: { label: false } },
      });

      setValidatedObject(value);

      if (error) {
        let errors = error.details.reduce((errorObject, errorDetail) => {
          errorObject[errorDetail.context.key] = errorDetail.message;
          return errorObject;
        }, {});
        setErrorMessages(errors);
      } else {
        setErrorMessages({});
      }
    }
  };

  const addKeyToBlurredFields = (key) => {
    setBlurredFields((prevBlurredFields) => [
      ...(prevBlurredFields ? prevBlurredFields.filter((k) => k !== key) : []),
      key,
    ]);
  };

  const resetValidation = () => {
    setBlurredFields([]);
  };

  let detailData = Object.entries(fields).map(([key, row]) => {
    let fieldShouldBeValidated = !!(row.fieldSchema || row.isRequired);

    return {
      ...row,
      key,
      value: row.value || object[key],
      editableFields: ['value'],
      ...(row.isEmail && { emailFields: ['value'] }),
      fieldShouldBeValidated,
      ...(fieldShouldBeValidated && {
        errorMessage:
          blurredFields && blurredFields.includes(key)
            ? errorMessages[key]
            : undefined,
        blurField: () => addKeyToBlurredFields(key),
      }),
    };
  });

  const getValidatedDetailTable = (inEditState) => (
    <DetailTable data={detailData} editable={inEditState} />
  );

  const errorMessageListComponent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        fontSize: 'small',
        padding: 4,
      }}>
      {Object.values(errorMessages).map((msg, index) => (
        <div key={index}>{msg}</div>
      ))}
    </div>
  );

  let isDataInvalid = !!Object.entries(errorMessages).length;
  const ValidatedSaveButton = ({ doSave }) => (
    <ConditionalWrapper
      wrapper={(children) => (
        <Tooltip content={errorMessageListComponent} hoverCloseDelay={0}>
          {children}
        </Tooltip>
      )}
      condition={isDataInvalid}>
      <SaveButton clickHandler={doSave} disabled={isDataInvalid} />
    </ConditionalWrapper>
  );

  ValidatedSaveButton.propTypes = {
    doSave: PropTypes.func.isRequired,
  };

  return {
    validatedObject,
    getValidatedDetailTable,
    ValidatedSaveButton,
    resetValidation,
  };
};
